import { useSelector } from 'react-redux';
import { RequestStatusName, RootState } from '../storeTypes';
import { RequestStatus, isFailed, isInProcess, isSucceeded } from '../utils/requestStatus';

export const getCurrentPatientIdSelector = (state: RootState) =>
  state.appState.currentPatientId;

export const getSelectedCaseIdSelector = (state: RootState) => state.appState.selectedCaseId;

export const getRequestStatusSelector = (requestStatusName: RequestStatusName) => {
  const requestStatusSelector: (state: RootState) => RequestStatus = (state: RootState) =>
    state.appState.requestsStatus[requestStatusName];
  return requestStatusSelector;
};

export const getMeasurementSystemSelector = (state: RootState) =>
  state.appState.measurementSystem;

export const getAppVersion = (state: RootState) => state.appState.appVersion;

/**
 * Reduce to a BOOLEAN inside the selector, never outside it.
 *
 * This used to be `getRequestsStatusSelector(names)`, which returned a selector that
 * built a NEW ARRAY on every call:
 *
 *     requestStatusNames.map((n) => state.appState.requestsStatus[n])
 *
 * react-redux compares a selector's result by reference, so a fresh array is always
 * "changed" — every one of these four hooks therefore re-rendered its component on
 * EVERY DISPATCHED ACTION, whatever the action was. That is 8 call sites in the
 * patient form, 9 in the clinician form and 5 in the plan form, all re-rendering on
 * traffic that has nothing to do with them.
 *
 * Returning a primitive fixes it by construction: two calls over unchanged state now
 * produce the same value, so react-redux bails out. The `some(...)` is unchanged, it
 * has simply moved inside.
 */
function useRequestStatusPredicate(
  id: string | undefined,
  requestStatusNames: RequestStatusName[],
  predicate: (args: { id: string; requestStatus: RequestStatus }) => boolean,
): boolean {
  return useSelector((state: RootState) => {
    if (!id) {
      return false;
    }
    return requestStatusNames.some((requestStatusName) =>
      predicate({
        id,
        requestStatus: state.appState.requestsStatus[requestStatusName],
      }),
    );
  });
}

export function useInProcess(id: string | undefined, requestStatusNames: RequestStatusName[]) {
  return useRequestStatusPredicate(id, requestStatusNames, isInProcess);
}

export function useIsEndedSuccessfully(
  id: string | undefined,
  requestStatusNames: RequestStatusName[],
) {
  return useRequestStatusPredicate(id, requestStatusNames, isSucceeded);
}

export function useIsFinished(
  id: string | undefined,
  requestStatusNames: RequestStatusName[],
) {
  return useRequestStatusPredicate(
    id,
    requestStatusNames,
    ({ id: rowId, requestStatus }) =>
      !isInProcess({ id: rowId, requestStatus }) &&
      (isFailed({ id: rowId, requestStatus }) || isSucceeded({ id: rowId, requestStatus })),
  );
}

export function useIsFailed(id: string | undefined, requestStatusNames: RequestStatusName[]) {
  return useRequestStatusPredicate(id, requestStatusNames, isFailed);
}

export const getUserIdSelector = (state: RootState) => state.appState.userId;

export const getEditTemplateIdSelector = (state: RootState) => state.appState.editTemplateId;

export const getUserRoleSelector = (state: RootState) => state.appState.role;
export const isUnrestrictedUserSelector = (state: RootState) =>
  state.appState.role === 'admin' || state.appState.role === 'clinician';
export const isAdminSelector = (state: RootState) => state.appState.role === 'admin';
export const isPatientSelector = (state: RootState) => state.appState.role === 'patient';
